export type VoucherRedemptionGuideId = 'shoprite-checkers' | 'eezi-power' | 'eezi-airtime';

export interface VoucherRedemptionGuide {
  id: VoucherRedemptionGuideId;
  title: string;
  summary: string;
  ussdCode: string;
  ussdLabel: string;
  steps: string[];
  alternatives: Array<{ label: string; href?: string; external?: boolean }>;
  helpline?: { label: string; display: string; tel: string };
  /** Format PIN in groups (eeziAirtime: 3×4 digits) */
  pinGroupSize?: number;
  /** Full dial string = prefix + voucher PIN + suffix */
  ussdDialPattern?: {
    prefix: string;
    suffix: string;
    examplePin: string;
  };
  /** When false, USSD is not the primary redemption path (e.g. Shoprite Money Market registration only). */
  showRecommendedUssd?: boolean;
}

export function buildVoucherUssdDialString(guide: VoucherRedemptionGuide, pin: string): string | null {
  if (!guide.ussdDialPattern) return null;
  const digits = pin.replace(/\D/g, '');
  if (!digits) return null;
  return `${guide.ussdDialPattern.prefix}${digits}${guide.ussdDialPattern.suffix}`;
}

export const VOUCHER_REDEMPTION_GUIDES: Record<VoucherRedemptionGuideId, VoucherRedemptionGuide> = {
  'shoprite-checkers': {
    id: 'shoprite-checkers',
    title: 'How to use your Shoprite / Checkers voucher',
    summary: 'Use the voucher code shown above at the till, in the Shoprite app, or as directed by OTT SMS. The code may be 7 digits or longer depending on what OTT returns.',
    ussdCode: '*120*3534#',
    ussdLabel: 'Shoprite Money Market account USSD',
    showRecommendedUssd: false,
    steps: [
      'Copy each voucher code shown above (for example a short PIN and/or a longer in-store number).',
      'Visit any Shoprite or Checkers store and present the voucher code at the till when you pay.',
      'If OTT sends an SMS with a voucher number, use that code when staff or the till prompt for it.',
      'You can also try the Shoprite app or WhatsApp links below if they apply to your voucher type.',
    ],
    alternatives: [
      { label: 'Shoprite app', href: 'https://bit.ly/3nni5HM', external: true },
      { label: 'WhatsApp', href: 'https://bit.ly/2SSMKCT', external: true },
      { label: 'Visit any Shoprite or Checkers store' },
      { label: 'Shoprite Money Market account USSD: *120*3534# (optional — opens a Money Market account; not for loading this voucher PIN)' },
    ],
    helpline: {
      label: 'Shoprite voucher helpline',
      display: '0860 010 709',
      tel: '0860010709',
    },
  },
  'eezi-power': {
    id: 'eezi-power',
    title: 'How to claim your eeziPower electricity token',
    summary: 'Dial the free USSD code on your phone. Have your meter number and eeziPower token ready.',
    ussdCode: '*130*269#',
    ussdLabel: 'eeziPower USSD (free)',
    steps: [
      'Copy your eeziPower token shown above (from your purchase / Flash slip).',
      'Dial *130*269# on your mobile phone.',
      'Select option 1 (Eezi Power) on the menu.',
      'Enter your prepaid electricity meter number.',
      'Enter your eeziPower token when prompted.',
      'Wait for validation — your electricity PIN will be sent to you by SMS.',
    ],
    alternatives: [
      { label: 'Visit any Flash partner retailer for in-store assistance' },
    ],
  },
  'eezi-airtime': {
    id: 'eezi-airtime',
    title: 'How to use your eeziAirtime voucher',
    summary: 'Dial the USSD string with your voucher PIN from the phone you want to top up.',
    ussdCode: '*130*3621*3*[PIN]#',
    ussdLabel: 'eeziAirtime USSD (include your voucher PIN)',
    pinGroupSize: 4,
    ussdDialPattern: {
      prefix: '*130*3621*3*',
      suffix: '#',
      examplePin: '123456789012',
    },
    steps: [
      'Copy your eeziAirtime voucher PIN shown above.',
      'Open your phone dialer and enter *130*3621*3* followed by your voucher PIN and #.',
      'Example: *130*3621*3*123456789012#',
      'Press the call/send button.',
    ],
    alternatives: [
      { label: 'Visit any Flash partner retailer for in-store assistance' },
    ],
  },
};

export function resolveVoucherRedemptionGuide(input: {
  catalogKey?: string | null;
  brand?: string | null;
  name?: string | null;
  description?: string | null;
  productName?: string | null;
  operationType?: string | null;
  isEeziPowerToken?: boolean | null;
  isEeziAirtimeToken?: boolean | null;
}): VoucherRedemptionGuide | null {
  if (input.isEeziAirtimeToken === true || input.operationType === 'eezi_airtime_token') {
    return VOUCHER_REDEMPTION_GUIDES['eezi-airtime'];
  }

  if (input.isEeziPowerToken === true || input.operationType === 'eezi_power_token') {
    return VOUCHER_REDEMPTION_GUIDES['eezi-power'];
  }

  const catalogKey = String(input.catalogKey || '').toLowerCase();
  if (catalogKey === 'shoprite-checkers') {
    return VOUCHER_REDEMPTION_GUIDES['shoprite-checkers'];
  }

  const haystack = [
    input.brand,
    input.name,
    input.description,
    input.productName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/eezipower|eezi power/.test(haystack) && !/eeziairtime|eezi airtime/.test(haystack)) {
    return VOUCHER_REDEMPTION_GUIDES['eezi-power'];
  }

  if (/eeziairtime|eezi airtime/.test(haystack) && !/eezipower|eezi power/.test(haystack)) {
    return VOUCHER_REDEMPTION_GUIDES['eezi-airtime'];
  }

  if (/shoprite|checkers/.test(haystack)) {
    return VOUCHER_REDEMPTION_GUIDES['shoprite-checkers'];
  }

  return null;
}
